<template>
  <Teleport to="body">
    <Transition name="notif-modal">
      <div
        v-if="notification"
        class="notif-modal-backdrop"
        @click.self="$emit('close')"
      >
        <div class="notif-modal" role="dialog" aria-modal="true" :aria-labelledby="`notif-title-${notification.id}`">
          <div class="notif-modal-accent" :class="`accent-${type}`"></div>

          <div class="notif-modal-header">
            <div class="notif-modal-icon" :style="{ color: category.color, backgroundColor: `${category.color}1f` }">
              <i :class="['bi', category.icon]"></i>
            </div>
            <div class="notif-modal-heading">
              <div class="notif-pills">
                <span class="notif-type-pill notif-category-pill" :style="{ color: category.color, backgroundColor: `${category.color}1f` }">{{ category.label }}</span>
                <span class="notif-type-pill" :class="`pill-${type}`">{{ typeLabel }}</span>
                <span v-if="notification.priority === 'high'" class="notif-type-pill pill-error">
                  <i class="bi bi-lightning-charge-fill"></i> Important
                </span>
              </div>
              <h5 :id="`notif-title-${notification.id}`" class="notif-modal-title">
                {{ notification.title || 'Notification' }}
              </h5>
              <div class="notif-modal-date">
                <i class="bi bi-calendar3 me-1"></i>{{ fullDate }}
              </div>
            </div>
            <button class="notif-modal-close" type="button" title="Fermer (Échap)" @click="$emit('close')">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="notif-modal-body">
            <div v-if="notification.actor || notification.project" class="notif-context">
              <span v-if="notification.actor" class="notif-context-item">
                <span class="notif-avatar">{{ initials(notification.actor.name) }}</span>
                <span><small>Par</small> {{ notification.actor.name }}</span>
              </span>
              <span v-if="notification.project" class="notif-context-item">
                <i class="bi bi-building"></i>
                <span><small>Projet</small> {{ notification.project.name }}</span>
              </span>
            </div>

            <p class="notif-modal-message">{{ notification.message }}</p>

            <dl v-if="details.length" class="notif-details">
              <template v-for="[label, value] in details" :key="label">
                <dt>{{ label }}</dt>
                <dd>{{ value }}</dd>
              </template>
            </dl>

            <button
              v-if="notification.action_url"
              type="button"
              class="notif-action-btn"
              @click="$emit('open-action', notification)"
            >
              {{ notification.action_label || 'Ouvrir' }}
              <i class="bi bi-arrow-right"></i>
            </button>
          </div>

          <div class="notif-modal-footer">
            <div class="d-flex gap-2">
              <button class="notif-btn" type="button" @click="copyMessage">
                <i :class="copied ? 'bi bi-check2' : 'bi bi-clipboard'"></i>
                {{ copied ? 'Copié' : 'Copier' }}
              </button>
              <button class="notif-btn" type="button" @click="$emit('toggle-read', notification)">
                <i :class="isRead ? 'bi bi-envelope' : 'bi bi-envelope-open'"></i>
                {{ isRead ? 'Marquer non lu' : 'Marquer lu' }}
              </button>
              <button class="notif-btn notif-btn-danger" type="button" @click="$emit('delete', notification)">
                <i class="bi bi-trash"></i>
                Supprimer
              </button>
            </div>
            <div class="d-flex gap-2 ms-auto">
              <button class="notif-btn notif-btn-icon" type="button" :disabled="!hasPrev" title="Précédente" @click="$emit('prev')">
                <i class="bi bi-chevron-left"></i>
              </button>
              <button class="notif-btn notif-btn-icon" type="button" :disabled="!hasNext" title="Suivante" @click="$emit('next')">
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { useNotifications } from '../composables/useNotifications'

const TYPES = {
  success: { icon: 'bi bi-check-circle-fill', label: 'Succès' },
  error: { icon: 'bi bi-exclamation-octagon-fill', label: 'Erreur' },
  warning: { icon: 'bi bi-exclamation-triangle-fill', label: 'Avertissement' },
  info: { icon: 'bi bi-info-circle-fill', label: 'Information' }
}

export default {
  name: 'NotificationDetailModal',
  props: {
    notification: { type: Object, default: null },
    hasPrev: { type: Boolean, default: false },
    hasNext: { type: Boolean, default: false }
  },
  emits: ['close', 'toggle-read', 'delete', 'prev', 'next', 'open-action'],
  setup() {
    const { categoryMeta, loadCatalog } = useNotifications()
    loadCatalog()
    return { categoryMeta }
  },
  data() {
    return { copied: false }
  },
  computed: {
    type() {
      return TYPES[this.notification?.type] ? this.notification.type : 'info'
    },
    icon() {
      return TYPES[this.type].icon
    },
    typeLabel() {
      return TYPES[this.type].label
    },
    category() {
      return this.categoryMeta(this.notification?.category)
    },
    // Contexte métier (motif, date du rapport...) : liste [{label, value}], valeurs simples seulement
    details() {
      const data = this.notification?.data
      if (!data || typeof data !== 'object') return []
      const entries = Array.isArray(data)
        ? data.filter(item => item && typeof item === 'object').map(item => [item.label, item.value])
        : Object.entries(data)
      return entries.filter(([label, v]) => label && v !== null && v !== undefined && v !== '' && typeof v !== 'object')
    },
    isRead() {
      return Boolean(this.notification?.is_read ?? this.notification?.read)
    },
    fullDate() {
      const ts = this.notification?.created_at || this.notification?.timestamp
      if (!ts) return ''
      return new Date(ts).toLocaleString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },
  watch: {
    notification(value) {
      this.copied = false
      document.body.style.overflow = value ? 'hidden' : ''
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = ''
  },
  methods: {
    onKeydown(e) {
      if (!this.notification) return
      if (e.key === 'Escape') this.$emit('close')
      else if (e.key === 'ArrowLeft' && this.hasPrev) this.$emit('prev')
      else if (e.key === 'ArrowRight' && this.hasNext) this.$emit('next')
    },
    initials(name) {
      return String(name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    },
    async copyMessage() {
      const lines = [this.notification.title, this.notification.message, ...this.details.map(([k, v]) => `${k} : ${v}`)]
      const text = lines.filter(Boolean).join('\n\n')
      try {
        await navigator.clipboard.writeText(text)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      } catch (error) {
        console.error('Copy failed:', error)
      }
    }
  }
}
</script>

<style scoped>
.notif-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.notif-modal {
  position: relative;
  width: 100%;
  max-width: 640px;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  background: var(--bg-card, #fff);
  color: var(--text-main, #0f172a);
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3);
  overflow: hidden;
}

.notif-modal-accent {
  height: 5px;
  flex-shrink: 0;
}
.accent-info { background: linear-gradient(90deg, #3b82f6, #6366f1); }
.accent-success { background: linear-gradient(90deg, #10b981, #34d399); }
.accent-warning { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.accent-error { background: linear-gradient(90deg, #ef4444, #f97316); }

.notif-modal-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  flex-shrink: 0;
}

.notif-modal-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
}
.icon-success { background: rgba(16, 185, 129, 0.12); color: #10b981; }
.icon-error { background: rgba(239, 68, 68, 0.12); color: #ef4444; }
.icon-warning { background: rgba(245, 158, 11, 0.12); color: #f59e0b; }
.icon-info { background: rgba(59, 130, 246, 0.12); color: #3b82f6; }

.notif-modal-heading {
  flex: 1;
  min-width: 0;
}

.notif-type-pill {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 999px;
  margin-bottom: 6px;
}
.pill-success { background: rgba(16, 185, 129, 0.12); color: #059669; }
.pill-error { background: rgba(239, 68, 68, 0.12); color: #dc2626; }
.pill-warning { background: rgba(245, 158, 11, 0.15); color: #b45309; }
.pill-info { background: rgba(59, 130, 246, 0.12); color: #2563eb; }

.notif-modal-title {
  margin: 0 0 4px;
  font-weight: 700;
  font-size: 1.1rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.notif-modal-date {
  font-size: 0.78rem;
  color: var(--text-muted, #64748b);
  text-transform: capitalize;
}

.notif-modal-close {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--text-muted, #64748b);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.notif-modal-close:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.notif-modal-body {
  padding: 20px 22px;
  overflow-y: auto;
  flex: 1;
}

.notif-modal-message {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.7;
  white-space: pre-line;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.notif-modal-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid var(--border-color, #e2e8f0);
  flex-shrink: 0;
}

.notif-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  font-size: 0.82rem;
  font-weight: 500;
  border-radius: 9px;
  border: 1px solid var(--border-color, #e2e8f0);
  background: transparent;
  color: var(--text-main, #0f172a);
  transition: all 0.2s ease;
}
.notif-btn:hover:not(:disabled) {
  border-color: #6366f1;
  color: #6366f1;
  background: rgba(99, 102, 241, 0.06);
}
.notif-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.notif-btn-danger:hover:not(:disabled) {
  border-color: #ef4444;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.06);
}
.notif-btn-icon {
  padding: 7px 10px;
}

.notif-modal-enter-active,
.notif-modal-leave-active {
  transition: opacity 0.2s ease;
}
.notif-modal-enter-active .notif-modal,
.notif-modal-leave-active .notif-modal {
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.notif-modal-enter-from,
.notif-modal-leave-to {
  opacity: 0;
}
.notif-modal-enter-from .notif-modal,
.notif-modal-leave-to .notif-modal {
  transform: scale(0.95) translateY(10px);
}

@media (max-width: 576px) {
  .notif-modal-backdrop {
    align-items: flex-end;
    padding: 0;
  }
  .notif-modal {
    border-radius: 18px 18px 0 0;
    max-height: 90vh;
  }
  .notif-btn span,
  .notif-modal-footer .notif-btn {
    font-size: 0.78rem;
  }
}

.notif-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.notif-category-pill {
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.72rem;
}

.notif-context {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-bottom: 14px;
  font-size: 0.85rem;
}

.notif-context-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text-main, #0f172a);
}

.notif-context-item small {
  color: var(--text-muted, #64748b);
  margin-right: 2px;
}

.notif-context-item > .bi {
  color: var(--text-muted, #64748b);
}

.notif-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
}

.notif-details {
  display: grid;
  grid-template-columns: minmax(120px, max-content) 1fr;
  gap: 8px 16px;
  margin: 16px 0 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(100, 116, 139, 0.07);
  font-size: 0.86rem;
}

.notif-details dt {
  font-weight: 500;
  color: var(--text-muted, #64748b);
}

.notif-details dd {
  margin: 0;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.notif-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  padding: 9px 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #4f46e5, #6366f1);
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: 0 6px 16px rgba(79, 70, 229, 0.25);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.notif-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.32);
}

@media (max-width: 576px) {
  .notif-details {
    grid-template-columns: 1fr;
    gap: 2px;
  }
  .notif-details dd {
    margin-bottom: 8px;
  }
}
</style>
